import * as React from "react";
import CompassNameIcon from "../images/general/CgiNameIconBlack.svg";
import CompassLogoGradient from "../images/general/CompassIconGradient.svg";
import SpecialText from "../components/SpecialText";
import { useTranslation } from "react-i18next";
import { Box, Button, Divider, Grid, Icon, Typography } from "@mui/material";
import {
  CGI_BLACK,
  CGI_GRADIENT_A_45DEG,
  CGI_GRADIENT_B,
  CGI_SUCCESS,
  COMPASS_GRADIENT,
} from "../helpers/enums";

export default function ModuleSelect({
  assessmentData,
  visitedQuestions,
  setSelectedModule,
  finishAssessment,
}) {
  const { t } = useTranslation();
  const assessmentModules = assessmentData?.assessmentModules
    ? JSON.parse(assessmentData?.assessmentModules)
    : {};

  function allQuestionsAnswered() {
    let questions = 0;
    let answers = 0;
    Object.values(assessmentModules).forEach((module) => {
      Object.values(module.questions).forEach((question) => {
        questions++;
        if (visitedQuestions.includes(question.id)) answers++;
      });
    });
    return questions === answers;
  }

  return (
    <Box
      sx={{
        display: "flex",
        flexDirection: "column",
      }}
    >
      <Box
        sx={{
          display: "flex",
          flexDirection: "column",
          alignItems: "center",
          minHeight: "55vh",
        }}
      >
        <Box
          sx={{
            display: "flex",
            flexDirection: "row",
            alignItems: "center",
            width: { xs: "auto", md: 1 },
            mt: { xs: 5, md: -13 },
            ml: 5,
          }}
        >
          <Icon sx={{ width: "auto", height: "auto" }}>
            <img draggable={false} src={CompassLogoGradient} height={"55px"} />
          </Icon>
          <Icon sx={{ width: "auto", height: "auto", ml: 2 }}>
            <img draggable={false} src={CompassNameIcon} height="50px" />
          </Icon>
        </Box>
        <Typography
          align="center"
          sx={{ fontSize: 23, mt: 7, fontStyle: "italic", pr: 7 }}
        >
          CGI Compass Assessment
        </Typography>
        <Typography align="center" sx={{ fontSize: 30, mt: 2, mb: 9 }}>
          <SpecialText
            style={{
              background: CGI_GRADIENT_A_45DEG,
              WebkitBackgroundClip: "text",
              WebkitTextFillColor: "transparent",
            }}
          >
            {assessmentData?.title}
          </SpecialText>
        </Typography>
        <Grid
          container
          spacing={1}
          rowSpacing={5}
          justifyContent="center"
          alignItems="center"
        >
          {Object.values(assessmentModules).length === 0 && (
            <Typography sx={{ fontSize: 18, mt: 15 }}>
              {t("noModule")}
            </Typography>
          )}
          {Object.values(assessmentModules).map((module, index) => {
            const questions = Object.values(module.questions);
            const answerCount = questions.filter((x) =>
              visitedQuestions.includes(x.id)
            ).length;
            const questionCount = questions.length;
            return (
              <React.Fragment key={index}>
                {module?.questions.length !== 0 && (
                  <Grid
                    item
                    key={index}
                    xs={12}
                    md={6}
                    lg={4}
                    sx={{
                      flexDirection: "column",
                      display: "flex",
                      justifyContent: "center",
                      alignItems: "center",
                    }}
                  >
                    <Box
                      sx={{
                        display: "flex",
                        flexDirection: "column",
                        justifyContent: "center",
                        alignItems: "center",
                        width: 7 / 10,
                        borderRadius: 20,
                        border: "double 1px transparent",
                        backgroundImage:
                          questionCount === answerCount
                            ? "linear-gradient(white, white)," +
                              COMPASS_GRADIENT
                            : "linear-gradient(white, white)," + CGI_GRADIENT_B,
                        backgroundOrigin: "border-box",
                        backgroundClip: "content-box, border-box",
                      }}
                    >
                      <Box
                        sx={{
                          display: "flex",
                          flexDirection: "column",
                          justifyContent: "center",
                          alignItems: "center",
                          width: 0.85,
                          borderRadius: 20,
                          border: "double 3px transparent",
                          backgroundImage:
                            questionCount === answerCount
                              ? "linear-gradient(white, white)," +
                                COMPASS_GRADIENT
                              : "linear-gradient(white, white)," +
                                CGI_GRADIENT_B,
                          backgroundOrigin: "border-box",
                          backgroundClip: "content-box, border-box",
                          m: 2.5,
                          "&:hover": {
                            cursor: "pointer",
                            transform: "scale(1.05)",
                            boxShadow: 4,
                          },
                        }}
                      >
                        <Box
                          onClick={() => setSelectedModule(module)}
                          sx={{
                            display: "flex",
                            flexDirection: "column",
                            justifyContent: "center",
                            alignItems: "center",
                            width: 1,
                            py: 1.5,
                          }}
                        >
                          <Typography
                            sx={{
                              textAlign: "center",
                              fontSize: 16,
                              fontWeight: 500,
                              userSelect: "none",
                              px: 3,
                            }}
                          >
                            {module.title}
                          </Typography>
                          <Divider
                            flexItem
                            sx={{
                              my: 1,
                              mx: 2,
                              borderImageSlice: 1,
                              borderImageSource:
                                questionCount === answerCount
                                  ? COMPASS_GRADIENT
                                  : CGI_GRADIENT_B,
                            }}
                          />
                          <Box
                            sx={{
                              display: "flex",
                              flexDirection: "row",
                              width: 1,
                              justifyContent: "center",
                            }}
                          >
                            <Typography
                              align="center"
                              sx={{
                                mx: 2,
                                mt: 1,
                                userSelect: "none",
                                color:
                                  questionCount === answerCount
                                    ? CGI_SUCCESS
                                    : CGI_BLACK,
                              }}
                            >
                              {answerCount}/{questionCount}{" "}
                              {t("questionsAnswered")}
                            </Typography>
                          </Box>
                        </Box>
                      </Box>
                    </Box>
                  </Grid>
                )}
              </React.Fragment>
            );
          })}
        </Grid>
      </Box>
      <Box
        sx={{ display: "flex", flexDirection: "row", justifyContent: "center" }}
      >
        <Button
          disabled={!allQuestionsAnswered()}
          variant="contained"
          sx={{ mt: 10, borderRadius: 20, border: 3, fontSize: 16 }}
          onClick={() => finishAssessment()}
        >
          {t("finishAssessment")}
        </Button>
      </Box>
    </Box>
  );
}
